import React, { useEffect, useMemo, useRef, useState } from "react";
import OverlayPortal from "./OverlayPortal";
import { AchievementToast, useAchievements } from "./AchievementsProvider";
import { useSidekick } from "./SidekickOverlay";
import { isPlayGamesSignedIn } from "../lib/playGamesAchievements";
import { useViewport } from "../hooks/useViewport";

const TOAST_DURATION_MS = 4000;
const TOAST_GAP_MS = 700;

function getAssetPath(path: string): string {
  const cleanPath = path.replace(/^\//, "");
  const isBrowser = typeof window !== "undefined";

  const cap = isBrowser ? (window as any).Capacitor : null;
  const isNative =
    Boolean(cap) &&
    (typeof cap.isNativePlatform === "function"
      ? cap.isNativePlatform()
      : typeof cap.getPlatform === "function"
        ? cap.getPlatform() === "ios" || cap.getPlatform() === "android"
        : cap.platform === "ios" || cap.platform === "android");

  if (isNative) {
    try {
      const baseUrl = new URL(window.location.href);
      return baseUrl.origin + "/" + cleanPath;
    } catch (e) {
      console.warn("[getAssetPath] Failed to construct URL, using fallback:", e);
      return "/" + cleanPath;
    }
  }

  return "./" + cleanPath;
}

function buildIconLabel(toast: AchievementToast): string {
  const words = toast.name.split(" ").filter(Boolean);
  if (words.length === 0) return "A";
  const letters = words.map((word) => word[0].toUpperCase()).join("");
  return letters.slice(0, 2);
}

export default function AchievementsToastHost() {
  const { toastQueue, dequeueToast, safeToShow } = useAchievements();
  const { isSidekickActive } = useSidekick();
  const viewport = useViewport();
  const [activeToast, setActiveToast] = useState<AchievementToast | null>(null);
  const [iconFailed, setIconFailed] = useState(false);
  const [containerRightOffset, setContainerRightOffset] = useState<number | null>(null);
  const lastToastEndedAtRef = useRef(0);
  const dismissTimerRef = useRef<number | null>(null);
  const showTimerRef = useRef<number | null>(null);

  useEffect(() => {
    if (!activeToast) return;
    if (dismissTimerRef.current) {
      window.clearTimeout(dismissTimerRef.current);
    }
    dismissTimerRef.current = window.setTimeout(() => {
      lastToastEndedAtRef.current = Date.now();
      setActiveToast(null);
    }, TOAST_DURATION_MS);

    return () => {
      if (dismissTimerRef.current) {
        window.clearTimeout(dismissTimerRef.current);
      }
    };
  }, [activeToast]);

  useEffect(() => {
    if (activeToast || !safeToShow || isSidekickActive) return;
    if (toastQueue.length === 0) return;

    const now = Date.now();
    const elapsed = now - lastToastEndedAtRef.current;
    const delay = Math.max(0, TOAST_GAP_MS - elapsed);

    if (showTimerRef.current) {
      window.clearTimeout(showTimerRef.current);
    }

    showTimerRef.current = window.setTimeout(() => {
      const next = dequeueToast();
      if (next) {
        setActiveToast(next);
      }
    }, delay);

    return () => {
      if (showTimerRef.current) {
        window.clearTimeout(showTimerRef.current);
      }
    };
  }, [activeToast, dequeueToast, isSidekickActive, safeToShow, toastQueue.length]);

  const iconLabel = useMemo(() => {
    if (!activeToast) return "";
    return buildIconLabel(activeToast);
  }, [activeToast]);

  useEffect(() => {
    setIconFailed(false);
  }, [activeToast?.icon_key]);

  useEffect(() => {
    if (typeof document === "undefined") return;
    const container = document.querySelector("[data-page-container]");
    if (!container) {
      setContainerRightOffset(null);
      return;
    }
    const rect = container.getBoundingClientRect();
    const viewportWidth = document.documentElement?.clientWidth || window.innerWidth;
    const offset = Math.max(0, viewportWidth - rect.right);
    setContainerRightOffset(offset);
  }, [activeToast, viewport.width, viewport.height]);

  if (!activeToast) {
    return null;
  }

  if (isPlayGamesSignedIn()) {
    return null;
  }

  const iconSrc = activeToast.icon_key
    ? getAssetPath(`achievement-icons/${activeToast.icon_key}`)
    : "";
  const containerMaxWidth = 1024;
  const containerPadding = viewport.width >= 768 ? 24 : 8;
  const fallbackOffset = Math.max(0, (viewport.width - containerMaxWidth) / 2);
  const rightOffset = (containerRightOffset ?? fallbackOffset) + containerPadding;

  return (
    <OverlayPortal>
      <div
        className="fixed flex justify-end"
        style={{
          top: "calc(env(safe-area-inset-top, 0px) + 12px)",
          right: `calc(env(safe-area-inset-right, 0px) + ${rightOffset}px)`,
          zIndex: 11000,
          pointerEvents: "none",
          width: "min(320px, 80vw)",
        }}
      >
        <div
          className="flex items-center gap-3 px-3 py-2 rounded-lg border-2 shadow-lg"
          style={{
            background: "#fffaeb",
            borderColor: "#8b6542",
            color: "#3e2615",
            transform: "translateY(0)",
            transition: "transform 0.2s ease, opacity 0.2s ease",
          }}
        >
          <div
            className="flex items-center justify-center"
            style={{
              width: "48px",
              height: "48px",
              background: iconFailed || !iconSrc ? "rgba(245, 214, 164, 0.9)" : "transparent",
              borderRadius: "10px",
              fontSize: "12px",
              fontWeight: 700,
              color: "#3e2615",
              overflow: "hidden",
            }}
          >
            {!iconFailed && iconSrc ? (
              <img
                src={iconSrc}
                alt=""
                style={{ width: "100%", height: "100%", objectFit: "cover" }}
                onError={() => setIconFailed(true)}
              />
            ) : (
              iconLabel
            )}
          </div>
          <div className="text-xs leading-tight">
            <div className="font-semibold">Achievement unlocked:</div>
            <div>{activeToast.name}</div>
          </div>
        </div>
      </div>
    </OverlayPortal>
  );
}
